import { CommonModule } from '@angular/common';
import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonsModule, IconModule, LoadingModule } from 'axis-components';
import { Logger } from '../_core/logger.service';
import { NotificationService } from '../_services/notification/notification.service';
import { PdfService } from '../_services/pdf/pdf.service';
import { RequestService } from '../_services/request/request.service';
import { UserService } from '../_services/user.service';
import { AnalyticsService } from '../_shared/analytics/analytics.service';
import { FormType, PDFGenerationStatus } from '../config/constants';
import { PipBaseRulesService } from '../state-disclosures/pip/services/pip-base-rules.service';
import { UimuiBaseRulesService } from '../state-disclosures/uimui/services/uimui-base-rules.service';
import {
  ThankYouScenario,
  DefaultTitle,
  BothElectronic,
  UmUimElectronicPipDeclined,
  PipElectronicUimDeclined,
  BothDeclined,
  PipOnlyElectronic,
  PipOnlyDeclined,
  Messages,
  FileNames,
  UimuiOnlyElectronic,
  UimuiOnlyDeclined,
} from './thank-you.constants';

@Component({
  selector: 'app-thank-you',
  standalone: true,
  templateUrl: './thank-you.component.html',
  imports: [
    ButtonsModule,
    IconModule,
    RouterModule,
    CommonModule,
    LoadingModule,
  ],
})
export class ThankYouComponent implements OnInit {
  // Event emitted to playground component when workflow completes
  @Output() workflowCompletePlayground = new EventEmitter<void>();

  title: string = DefaultTitle.Title;
  content = '';
  subcontent = '';
  showPIP = true;
  showUIUM = true;
  note = '';

  requestKey = '';
  role;

  downloadUMUIMInProgress = false;
  downloadPIPInProgress = false;

  scenario: ThankYouScenario;
  isUimuiCompleted = false;
  isPipCompleted = false;

  bothElectronicContent = {
    title: BothElectronic.Title,
    content: BothElectronic.Content,
  };
  uimuiElectronicPipDeclinedContent = {
    title: UmUimElectronicPipDeclined.Title,
    content: UmUimElectronicPipDeclined.Content,
    nonDigitalContent: UmUimElectronicPipDeclined.NonDigitalContent,
  };
  pipElectronicUimuiDeclinedContent = {
    title: PipElectronicUimDeclined.Title,
    content: PipElectronicUimDeclined.Content,
    nonDigitalContent: PipElectronicUimDeclined.NonDigitalContent,
  };
  bothDeclinedContent = {
    title: BothDeclined.Title,
    content: BothDeclined.Content,
  };
  pipOnlyElectronicContent = {
    title: PipOnlyElectronic.Title,
    content: PipOnlyElectronic.Content,
  };
  pipOnlyDeclinedContent = {
    title: PipOnlyDeclined.Title,
    content: PipOnlyDeclined.Content,
  };
  uimuiOnlyElectronicContent = {
    title: UimuiOnlyElectronic.Title,
    content: UimuiOnlyElectronic.Content,
  };
  uimuiOnlyDeclinedContent = {
    title: UimuiOnlyDeclined.Title,
    content: UimuiOnlyDeclined.Content,
  };

  constructor(
    public route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private requestService: RequestService,
    private pdfService: PdfService,
    private analyticsService: AnalyticsService,
    private notificationService: NotificationService,
    private baseRulesService: PipBaseRulesService,
    private uimuiBaseRulesService: UimuiBaseRulesService,
  ) {}
  ngOnInit(): void {
    this.role = this.requestService.role;
    this.requestKey = this.requestService.getRequestKey() || '';
    // Skip completion check to prevent redirect to completed page during thank you navigation
    this.requestService.refreshRequestData(true).subscribe({
      next: (data) => {
        this.initializeFromApiData();
      },
      error: (err) => {
        console.error(Messages.ThankYouErrorMessage, err);
        // Fallback to existing data if API call fails
        this.initializeFromApiData();
      },
    });

    this.goTop();
  }

  private initializeFromApiData(): void {
    // Determine content based on formTypeStep data instead of query params
    this.determineScenarioFromApiData();
    this.contentFlow();
  }

  private determineScenarioFromApiData(): void {
    const formTypeList = this.requestService.requestFormTypeList;
    const hasUimui = formTypeList.includes('um_uim');
    const hasPip = formTypeList.includes('pip');
    this.isUimuiCompleted = hasUimui
      ? this.isFormTypeCompleted('um_uim')
      : false;
    this.isPipCompleted = hasPip ? this.isFormTypeCompleted('pip') : false;
    this.setScenarioFromCompletionStatus(hasUimui, hasPip);
  }

  private isFormTypeCompleted(formType: string): boolean {
    const config = this.requestService.getThankYouPageConfig(formType);
    return config.isDigital;
  }

  // Helper methods for template logic
  isPipNonDigital(): boolean {
    const pipConfig = this.requestService.getThankYouPageConfig('pip');
    return pipConfig.isNonDigitalDownload || pipConfig.isNonDigitalMail;
  }

  isUimuiNonDigital(): boolean {
    const uimuiConfig = this.requestService.getThankYouPageConfig('um_uim');
    return uimuiConfig.isNonDigitalDownload || uimuiConfig.isNonDigitalMail;
  }

  hasUimuiForm(): boolean {
    return this.requestService.requestFormTypeList.includes('um_uim');
  }

  hasPipForm(): boolean {
    return this.requestService.requestFormTypeList.includes('pip');
  }

  downloadPIPDocuments() {
    this.downloadPIPInProgress = true;
    this.analyticsService.logDownloadButtonClick(FormType.PIP);
    this.getPIPPdfFromGDMS();
  }

  getPIPPdfFromGDMS() {
    this.downloadPIPInProgress = true;
    this.pdfService.getPdfFromGDMS(FormType.PIP, this.requestKey).subscribe({
      next: (response) => {
        this.downloadPIPInProgress = false;
        this.handlePdfResponse(response, FileNames.PipDocuments);
      },
      error: (err) => {
        this.downloadPIPInProgress = false;
        console.error('Error generating PDF:', err);
        this.notificationService.showNotification(
          Messages.DocumentsUnavailableError,
          'error',
        );
      },
    });
  }


  handlePdfResponse(response: any, filename: string) {
    const blob = new Blob([response], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    // To download:
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);

    if (this.baseRulesService.isPlayground) {
      // Notify playground component that workflow is complete after brief delay
      setTimeout(() => {
        this.workflowCompletePlayground.emit();
      }, 1000);
    }
  }

  getUMUIMPdfFromGDMS() {
    this.downloadUMUIMInProgress = true;
    this.pdfService.getPdfFromGDMS(FormType.UM_UIM, this.requestKey).subscribe({
      next: (response) => {
        this.downloadUMUIMInProgress = false;
        this.handlePdfResponse(response, FileNames.UiUimDocuments);
      },
      error: (err) => {
        this.downloadUMUIMInProgress = false;
        console.error('Error generating PDF:', err);
        this.notificationService.showNotification(
          Messages.DocumentsUnavailableError,
          'error',
        );
      },
    });
  }

  downloadUMUIMDocuments() {
    this.downloadUMUIMInProgress = true;
    this.analyticsService.logDownloadButtonClick(FormType.UM_UIM);
    this.getUMUIMPdfFromGDMS();
  }

  goTop(): void {
    // reset the page when changing route
    window.scrollTo(0, 0);
  }
  contentFlow(): void {
    this.determineScenarioFromApiData();

    switch (this.scenario) {
      case ThankYouScenario.BothElectronic:
        this.title = this.bothElectronicContent.title;
        this.content = this.bothElectronicContent.content;
        this.showUIUM = true;
        this.showPIP = true;
        this.subcontent = Messages.EmailConfirmation;
        this.note = Messages.CloseBrowserNote;
        break;

      case ThankYouScenario.UimuiElectronicPipDeclined:
        this.title = this.uimuiElectronicPipDeclinedContent.title;
        this.content = this.uimuiElectronicPipDeclinedContent.content;
        this.showUIUM = true;
        this.showPIP = false;
        this.subcontent = Messages.EmailConfirmation;
        this.note = Messages.CloseBrowserNote;
        break;

      case ThankYouScenario.PipElectronicUimuiDeclined:
        this.title = this.pipElectronicUimuiDeclinedContent.title;
        this.content = this.pipElectronicUimuiDeclinedContent.content;
        this.showUIUM = false;
        this.showPIP = true;
        this.subcontent = Messages.EmailConfirmation;
        this.note = Messages.CloseBrowserNote;
        break;

      case ThankYouScenario.BothDeclined:
        this.title = this.bothDeclinedContent.title;
        this.content = this.bothDeclinedContent.content;
        this.showUIUM = false;
        this.showPIP = false;
        this.subcontent = '';
        this.note = Messages.CloseBrowserNote;
        break;

      case ThankYouScenario.PipOnlyElectronic:
        this.title = this.pipOnlyElectronicContent.title;
        this.content = this.pipOnlyElectronicContent.content;
        this.showUIUM = false;
        this.showPIP = true;
        this.subcontent = Messages.EmailConfirmation;
        this.note = '';
        break;

      case ThankYouScenario.PipOnlyDeclined:
        this.title = this.pipOnlyDeclinedContent.title;
        this.content = this.pipOnlyDeclinedContent.content;
        this.showUIUM = false;
        this.showPIP = true;
        this.subcontent = '';
        this.note = '';
        break;

      case ThankYouScenario.UimuiOnlyElectronic:
        this.title = this.uimuiOnlyElectronicContent.title;
        this.content = this.uimuiOnlyElectronicContent.content;
        this.showUIUM = true;
        this.showPIP = false;
        this.subcontent = Messages.EmailConfirmation;
        this.note = '';
        break;

      case ThankYouScenario.UimuiOnlyDeclined:
        this.title = this.uimuiOnlyDeclinedContent.title;
        this.content = this.uimuiOnlyDeclinedContent.content;
        this.showUIUM = true;
        this.showPIP = false;
        this.subcontent = '';
        this.note = '';
        break;
    }
  }

  downloadUnsignedDocuments(formType: 'uimui' | 'pip'): void {
    if (formType === 'uimui') {
      this.downloadUMUIMDocuments();
    } else if (formType === 'pip') {
      this.downloadPIPDocuments();
    }
  }

  private setScenarioFromCompletionStatus(
    hasUimui: boolean,
    hasPip: boolean,
  ): void {
    // Multi-form scenarios (both UM/UIM and PIP available)
    if (hasUimui && hasPip) {
      if (this.isUimuiCompleted && this.isPipCompleted) {
        this.scenario = ThankYouScenario.BothElectronic;
      } else if (this.isUimuiCompleted && !this.isPipCompleted) {
        this.scenario = ThankYouScenario.UimuiElectronicPipDeclined;
      } else if (!this.isUimuiCompleted && this.isPipCompleted) {
        this.scenario = ThankYouScenario.PipElectronicUimuiDeclined;
      } else {
        this.scenario = ThankYouScenario.BothDeclined;
      }
    }
    // Single-form scenarios
    else if (hasUimui && !hasPip) {
      // Only UM/UIM form workflow
      this.scenario = this.isUimuiCompleted
        ? ThankYouScenario.UimuiOnlyElectronic
        : ThankYouScenario.UimuiOnlyDeclined;
    } else if (!hasUimui && hasPip) {
      // Only PIP form workflow
      this.scenario = this.isPipCompleted
        ? ThankYouScenario.PipOnlyElectronic
        : ThankYouScenario.PipOnlyDeclined;
    } else {
      // No forms available - shouldn't happen
      console.warn('No forms available:', { hasUimui, hasPip });
      this.scenario = ThankYouScenario.BothElectronic;
    }
  }
}
